import { useState, type FormEvent } from "react";
import { brand } from "@/mocks/site";

const FORM_ID = "clue-submit-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbetjp14a1h3mr6hpg";

type Status = "idle" | "sending" | "success" | "error";

export default function SubmitForm({ fieldPrefix = "home" }: { fieldPrefix?: string }) {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const payload = new FormData(formEl);

    const honeypot = String(payload.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("已收到你的信息，我们会尽快整理归档。");
      formEl.reset();
      return;
    }

    payload.delete("company_alt");
    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    setStatus("sending");
    setMessage("正在提交…");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const failed =
        !response.ok ||
        (parsed?.code ? parsed.code !== "OK" : false) ||
        /spam/i.test(serverMsg);

      if (failed) {
        setStatus("error");
        setMessage(serverMsg || "提交未成功，请稍后再试，或直接发送邮件给我们。");
        return;
      }
      setStatus("success");
      setMessage("已收到你的信息，编辑会在 48 小时内核对并回复。");
      formEl.reset();
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未完成，请稍后再试。");
    }
  };

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full rounded-lg border border-background-200 bg-background-50 p-4 md:p-6"
      aria-label="线索投递与简报订阅"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor={`${fieldPrefix}-email`} className="text-xs font-medium text-foreground-700">
            邮箱地址 <span className="text-accent-600">*</span>
          </label>
          <input
            id={`${fieldPrefix}-email`}
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor={`${fieldPrefix}-type`} className="text-xs font-medium text-foreground-700">
            你想做什么
          </label>
          <select
            id={`${fieldPrefix}-type`}
            name="type"
            defaultValue="订阅观察简报"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="订阅观察简报">订阅观察简报</option>
            <option value="投递舆情线索">投递舆情线索</option>
            <option value="申请案例补充说明">申请案例补充说明</option>
            <option value="内容合作与转载">内容合作与转载</option>
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor={`${fieldPrefix}-content`} className="text-xs font-medium text-foreground-700">
          想补充的情况（选填，500 字以内）
        </label>
        <textarea
          id={`${fieldPrefix}-content`}
          name="content"
          rows={4}
          maxLength={500}
          placeholder="可以写下事件发生的大致时间、你看到的原始出处，以及哪些内容可以核实。请勿提交涉及个人隐私的信息。"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <div className="field-audit-note" aria-hidden="true">
        <label htmlFor={`${fieldPrefix}-company-alt`}>请勿填写此项</label>
        <input
          id={`${fieldPrefix}-company-alt`}
          name="company_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] leading-relaxed text-foreground-500">
          我们不会公开你的邮箱。涉及个人隐私的内容，一律不作收录。也可直接邮件至 {brand.clueEmail}
        </p>
        <button
          type="submit"
          disabled={status === "sending"}
          className="flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <i className="ri-send-plane-line text-base" aria-hidden="true" />
          {status === "sending" ? "提交中" : "提交"}
        </button>
      </div>

      {status !== "idle" && status !== "sending" && (
        <p
          role="status"
          className={`mt-3 rounded-md border px-3 py-2 text-xs ${
            status === "success"
              ? "border-primary-200 bg-primary-50 text-primary-800"
              : "border-accent-300 bg-accent-50 text-accent-900"
          }`}
        >
          {message}
        </p>
      )}
    </form>
  );
}